/**
 * Support Ticket API Types
 * Zod schemas for API response validation
 */

import { z } from '@spcc/core'

const noteSchema = z.object({
  author: z.string(),
  text: z.string(),
  createdAt: z.string().optional().default(''),
  at: z.string().optional().default(''),
}).transform(({ at, createdAt, ...rest }) => ({
  ...rest,
  createdAt: createdAt || at || '',
}))

const timelineEventSchema = z.object({
  type: z.enum([
    'created', 'status_changed', 'priority_changed', 'assigned', 'sla_recorded', 'note_added',
  ]),
  at: z.string(),
  actor: z.string().optional().nullable(),
  from: z.string().optional().nullable(),
  to: z.string().optional().nullable(),
  text: z.string().optional().nullable(),
})

const attachmentSchema = z.object({
  id: z.number(),
  documentId: z.string().optional(),
  name: z.string(),
  url: z.string(),
  mime: z.string().optional().default(''),
  size: z.number().optional().default(0),
})

const tenantRefSchema = z
  .object({
    id: z.union([z.number(), z.string()]).optional(),
    documentId: z.string().optional(),
    name: z.string().optional(),
  })
  .optional()
  .nullable()

const userRefSchema = z
  .object({
    id: z.union([z.number(), z.string()]).optional(),
    documentId: z.string().optional(),
    username: z.string().optional(),
    email: z.string().optional(),
  })
  .optional()
  .nullable()

const factoryRefSchema = z
  .object({
    id: z.union([z.number(), z.string()]).optional(),
    documentId: z.string().optional(),
    name: z.string().optional(),
    code: z.string().optional(),
  })
  .optional()
  .nullable()

export const apiSupportTicketSchema = z.object({
  id: z.number(),
  documentId: z.string().optional(),
  ticketNumber: z.string(),
  subject: z.string(),
  description: z.string(),
  category: z.enum([
    'order_issue', 'delivery_issue', 'payment_issue', 'account_issue',
    'platform_bug', 'malfunctioning_hardware', 'feature_request', 'general', 'other',
  ]),
  channel: z.enum(['customer_to_spcc', 'spcc_to_525k']),
  priority: z.enum(['low', 'medium', 'high', 'urgent']),
  status: z.enum(['open', 'in_progress', 'waiting_on_reporter', 'resolved', 'closed']),
  sourcePortal: z.enum([
    'customer_portal', 'ecommerce_app', 'admin_portal', 'gate_portal', 'kiosk_portal',
  ]).optional().nullable(),
  referenceType: z.string().optional().nullable(),
  referenceId: z.string().optional().nullable(),
  contactEmail: z.string().optional().nullable(),
  contactPhone: z.string().optional().nullable(),
  notes: z.array(noteSchema).optional().nullable(),
  timeline: z.array(timelineEventSchema).optional().nullable(),
  attachments: z.array(attachmentSchema).optional().nullable(),
  assignedTo: z.string().optional().nullable(),
  resolvedAt: z.string().optional().nullable(),
  slaStatus: z.enum(['met', 'breached']).optional().nullable(),
  metadata: z.any().optional().nullable(),
  user: userRefSchema,
  tenant: tenantRefSchema,
  factory: factoryRefSchema,
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
})

export const supportTicketsResponseSchema = z.object({
  data: z.array(apiSupportTicketSchema),
  meta: z
    .object({
      pagination: z.object({
        page: z.number(),
        pageSize: z.number(),
        pageCount: z.number(),
        total: z.number(),
      }),
    })
    .optional(),
})

export const supportTicketResponseSchema = z.object({
  data: apiSupportTicketSchema,
  meta: z.object({}).optional(),
})

export type ApiSupportTicketDto = z.infer<typeof apiSupportTicketSchema>
export type SupportTicketsApiResponse = z.infer<typeof supportTicketsResponseSchema>
export type SupportTicketApiResponse = z.infer<typeof supportTicketResponseSchema>
